<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { t } from 'i18next';
import { sortUp } from '@/components/icons/icons';
import Button, { ButtonProps } from '@/components/buttons/Button.vue';
import { useResizeObserver } from '@vueuse/core';
import { isNumber } from 'lodash';

interface ShowMoreProps {
  collapsedHeight?: number;
  fade?: boolean | string;
  moreLabel?: string;
  lessLabel?: string;
  color?: string;
  buttonPosition?: 'top' | 'bottom';
}

const {
  collapsedHeight = undefined,
  moreLabel = t('ShowMore'),
  lessLabel = t('ShowLess'),
  fade = true,
  color = 'rgb(var(--v-theme-surface))',
  buttonPosition = 'bottom',
} = defineProps<ShowMoreProps>();

const slots = defineSlots<{
  default: [];
  preview: [];
}>();

const expanded = ref(false);
const box = ref<HTMLElement | null>(null);
const fullHeight = ref(0);
const showButton = ref(true);

const maxHeight = computed(() => {
  if (expanded.value) {
    return `${fullHeight.value}px`;
  }
  if (isNumber(collapsedHeight)) {
    return `${collapsedHeight}px`;
  }
  if (slots.preview) {
    return '0px';
  }
  return undefined;
});
const TRANSITION_DURATION = '500ms';
const containerStyle = computed(() => ({
  maxHeight: maxHeight.value,
  overflow: 'hidden',
  transition: `max-height ${TRANSITION_DURATION} ease`,
  willChange: 'max-height',
}));
const angle = computed(() => (expanded.value ? '0turn' : '0.5turn'));

function toggle() {
  expanded.value = !expanded.value;
}

async function measure() {
  const el = box.value;
  if (!el) return;

  fullHeight.value = el.scrollHeight;

  if (slots.preview || (collapsedHeight === 0 && slots.default)) {
    showButton.value = true;
  } else {
    showButton.value = el.scrollHeight > el.clientHeight + 1;
  }
}

onMounted(async () => {
  await nextTick();
  await measure();
});
watch(expanded, measure);
useResizeObserver(box, measure);

const buttonProps = computed<ButtonProps>(() => ({
  label: expanded.value ? lessLabel : moreLabel,
  variant: 'ghost',
  class: 'toggle mt-2',
  size: 'small',
  prependIcon: sortUp,
  onClick: (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    toggle();
  },
}));
</script>

<template>
  <div :class="['expandable', { 'is-collapsed': !expanded }]">
    <Button v-if="buttonPosition === 'top' && (showButton || expanded)" v-bind="buttonProps" />

    <slot v-if="$slots.preview" name="preview" />

    <div ref="box" class="expandable__box" :style="containerStyle">
      <div class="expandable__content">
        <slot />
      </div>

      <div
        v-if="showButton && !expanded && fade"
        class="expandable__fade"
        :style="{
          background: `linear-gradient(to bottom, transparent, ${color})`,
        }"
      />
    </div>

    <Button v-if="buttonPosition === 'bottom' && (showButton || expanded)" v-bind="buttonProps" />
  </div>
</template>

<style scoped>
.expandable__box {
  position: relative;
  overflow: hidden;
  max-height: none;
}

.expandable__fade {
  position: absolute;
  inset: auto 0 0 0;
  height: 12px;
  pointer-events: none;
}

.expandable__content {
  overflow: hidden; /* needed for correct calculation of the scrollHeight when content has negative margins (v-row) */
}

.toggle :deep(.v-icon) {
  transition: rotate v-bind(TRANSITION_DURATION) ease-in-out;
  rotate: v-bind(angle);
}
</style>
